import { isToday } from 'date-fns/isToday';
import React from 'react';

import { getZonedDate, ZonedDate } from '@eika-infrastruktur/date';
import { runningInRNWebView } from '@eika-infrastruktur/mobilbank-lib';
import { Button, ButtonTypes, LinkButton, StandardButton } from '@eika/button';
import { Icon, IconTypes } from '@eika/icon';
import { Link, LinkType } from '@eika/link';
import { RichIcon, RichIconTypes } from '@eika/rich-icon';

import { useConfig } from '../../defaultConfig';
import type { AccountId, ApprovalStatus, PaymentType } from '../../generated-types';
import useTranslation from '../../hooks/useTranslation';
import CenteredModalContent from '../shared/CenteredModalContent/CenteredModalContent';
import { ErrorCardWrapper } from '../shared/ErrorCardWrapper';
import TransactionSuccessResultInfo from './TransactionSuccessResultInfo';

import './TransactionModal.scss';
import './TransactionResultInfo.scss';

type RequestExceptionWithResponse = import('@eika-infrastruktur/request').RequestExceptionWithResponse;

export interface TransactionResultInfoProps {
    type: PaymentType | 'downpayment' | 'efaktura-sharing';
    error?: RequestExceptionWithResponse | null;
    status?: ApprovalStatus;
    retry: () => void;
    onClose: () => void;
    onContinue?: () => void;
    onRetryManually?: () => void;
    fromAccountId?: AccountId;
    paymentDate?: ZonedDate;
    redirectLinkAvtalegiro?: string;
    successButtons?: boolean;
    isPlural?: boolean;
    isBeingSentForApproval?: boolean;
}

const TransactionResultInfo: React.FC<TransactionResultInfoProps> = ({
    type,
    error,
    retry,
    status,
    fromAccountId,
    onClose,
    onContinue,
    onRetryManually,
    paymentDate = getZonedDate(),
    redirectLinkAvtalegiro,
    successButtons = false,
    isPlural = false,
    isBeingSentForApproval = false,
}) => {
    const {
        accountLink,
        futurePaymentsLink,

        loginSettingsLink,
    } = useConfig();
    const translated = useTranslation();

    const infoMessage = translated.paymentInfoMessages[type] ?? translated.paymentInfoMessages.PAYMENT;

    const checkTransactions = () => {
        onClose();
        if (fromAccountId) {
            window.location.href = `${accountLink!.url}?id=${fromAccountId.value}`;
        }
    };

    if (error) {
        switch (error.errorName) {
            case 'DUPLICATE_PAYMENT':
                return (
                    <div className="centered-modal-content sgw-card">
                        <RichIcon type={RichIconTypes.HAAND_HEIHEI} />
                        <h3 className="typography-h3 transaction-result-info__header">
                            {infoMessage?.notCompleted.title}
                        </h3>
                        <p className="transaction-result-info__description">{infoMessage?.notCompleted.description}</p>
                        <div className="transaction-result-info__warning-button-row">
                            {onRetryManually && (
                                <Button
                                    buttonType={ButtonTypes.STANDARD_NEGATIVE}
                                    onClick={onRetryManually}
                                    data-testid="dup-payment-change-payment-button"
                                >
                                    {infoMessage?.notCompleted.change}
                                </Button>
                            )}
                            {onContinue && (
                                <Button onClick={onContinue} data-testid="dup-payment-go-to-signing-button">
                                    {infoMessage?.notCompleted.continue}
                                </Button>
                            )}
                            <div>
                                <LinkButton onClick={onClose} data-testid="dup-payment-cancel-button">
                                    {translated.cancel}
                                </LinkButton>
                            </div>
                        </div>
                    </div>
                );
            default:
                return (
                    <CenteredModalContent>
                        <ErrorCardWrapper exception={error} isModal>
                            <Button onClick={retry}>{translated.ok}</Button>
                        </ErrorCardWrapper>
                    </CenteredModalContent>
                );
        }
    }

    switch (status) {
        case 'BIM_EXPIRED':
            return (
                <CenteredModalContent withMessage>
                    <Icon type={IconTypes.BANK_ID_PA_MOBIL_36} />
                    <h3 className="typography-h3">{translated.bankidExpired.header}</h3>
                    {runningInRNWebView ? (
                        <div>
                            <p>{translated.bankidExpired.nativeMessage}</p>
                            <p>{translated.bankidExpired.nativeMessage2}</p>
                            {loginSettingsLink && (
                                <Link linkType={LinkType.LARGE_LINK} href={loginSettingsLink.url}>
                                    {translated.bankidExpired.settingsLink}
                                </Link>
                            )}
                        </div>
                    ) : (
                        <p>{translated.bankidExpired.desktopMessage}</p>
                    )}
                    <StandardButton onClick={onClose}>{translated.close}</StandardButton>
                </CenteredModalContent>
            );
        case 'UNKNOWN':
        case 'REJECTED':
            return (
                <CenteredModalContent withMessage>
                    <Icon className="wallet-questionmark" type={IconTypes.WALLET_QUESTIONMARK} />
                    <h3 className="typography-h3">{infoMessage?.error.heading}</h3>
                    <p>{infoMessage?.error.description}</p>
                    {fromAccountId ? (
                        <>
                            {isToday(paymentDate ?? getZonedDate()) ? (
                                <Button onClick={checkTransactions} size="small">
                                    {infoMessage?.error.check}
                                </Button>
                            ) : (
                                <Link
                                    href={`${futurePaymentsLink?.url}?id=${fromAccountId.value}`}
                                    type={LinkType.BUTTON_SMALL_POSITIVE}
                                >
                                    {infoMessage?.error.checkFuturePayments}
                                </Link>
                            )}
                        </>
                    ) : (
                        <StandardButton onClick={onClose}>{translated.ok}</StandardButton>
                    )}
                </CenteredModalContent>
            );
        default:
            return (
                <TransactionSuccessResultInfo
                    type={type}
                    onClose={onClose}
                    status={status}
                    paymentDate={paymentDate}
                    redirectLinkAvtalegiro={redirectLinkAvtalegiro}
                    successButtons={successButtons}
                    isPlural={isPlural}
                    isBeingSentForApproval={isBeingSentForApproval}
                />
            );
    }
};

export default TransactionResultInfo;
